/**
 * Receipt Encryption Utilities
 *
 * Provides encryption for receipt data stored in localStorage using
 * the same AES-256-GCM encryption used for electronic signature keys.
 *
 * Features:
 * - PBKDF2 key derivation with 100,000 iterations
 * - AES-256-GCM encryption with unique salt and IV per encryption
 * - Version flag for handling legacy unencrypted data
 * - Backward compatibility with unencrypted data
 *
 * Security considerations:
 * - Data is encrypted at rest using the same password as signature keys
 * - Without a password, data is stored unencrypted for backward compatibility
 * - Each encryption uses unique random salt (16 bytes) and IV (12 bytes)
 */

import {
  encryptWithPassword,
  decryptWithPassword,
  type EncryptedData,
} from "@/lib/electronic-signature/crypto-utils";
import { STORAGE_KEYS } from "@/lib/storage-keys";

/**
 * Storage format version for encrypted receipt data
 */
export const RECEIPT_ENCRYPTION_VERSION = 2;

/**
 * LocalStorage key for storing encryption preference
 * Uses canonical key from storage-keys.ts
 */
export const RECEIPT_ENCRYPTION_ENABLED_KEY =
  STORAGE_KEYS.RECEIPT_ENCRYPTION_ENABLED;

/**
 * Storage container for encrypted receipt data
 */
export interface EncryptedReceiptContainer {
  /** Version of the encryption format */
  version: number;
  /** Whether the data is encrypted */
  encrypted: boolean;
  /** The encrypted data or plaintext data */
  data: EncryptedData | string;
  /** Timestamp of encryption */
  timestamp?: string;
}

/**
 * Check if receipt encryption is enabled in preferences
 *
 * SECURITY CHANGE (SEC-REQ-001): Encryption is now ENABLED by default
 * for all new users. Returns true if:
 * - Explicitly set to 'true' in localStorage
 * - Not explicitly set yet (new user) - defaults to true
 * - Returns false only if explicitly disabled
 */
export function isReceiptEncryptionEnabled(): boolean {
  try {
    const enabled = localStorage.getItem(RECEIPT_ENCRYPTION_ENABLED_KEY);
    // SEC-REQ-001: Default to true if not explicitly set (new users)
    if (enabled === null) {
      return true; // Default: encryption enabled for new users
    }
    return enabled === "true";
  } catch {
    return true; // SEC-REQ-001: Fail secure - default to enabled
  }
}

/**
 * Check if this is a first-time user (encryption preference not set)
 * Used to prompt for password creation on first use
 */
export function isFirstTimeUser(): boolean {
  try {
    const enabled = localStorage.getItem(RECEIPT_ENCRYPTION_ENABLED_KEY);
    return enabled === null;
  } catch {
    return false;
  }
}

/**
 * Enable or disable receipt encryption preference
 */
export function setReceiptEncryptionEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(
      RECEIPT_ENCRYPTION_ENABLED_KEY,
      enabled ? "true" : "false",
    );
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error(
        "[ReceiptEncryption] Failed to set encryption preference:",
        error,
      );
    }
  }
}

/**
 * Encrypt receipt data using AES-256-GCM with password-derived key
 *
 * @param data - JSON stringified receipt data to encrypt
 * @param password - Password for encryption (same as signature key password)
 * @returns JSON stringified encrypted container
 * @throws Error if encryption fails
 */
export async function encryptReceiptData(
  data: string,
  password: string,
): Promise<string> {
  if (!password || password.length < 8) {
    throw new Error(
      "Password must be at least 8 characters long for encryption",
    );
  }

  try {
    const encrypted = await encryptWithPassword(data, password);

    const container: EncryptedReceiptContainer = {
      version: RECEIPT_ENCRYPTION_VERSION,
      encrypted: true,
      data: encrypted,
      timestamp: new Date().toISOString(),
    };

    return JSON.stringify(container);
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error("[ReceiptEncryption] Encryption failed:", error);
    }
    throw new Error("Failed to encrypt receipt data");
  }
}

/**
 * Decrypt receipt data using AES-256-GCM with password-derived key
 *
 * Handles multiple scenarios:
 * 1. Encrypted data (version 2+) - decrypts with password
 * 2. Legacy unencrypted data - returns as-is for backward compatibility
 * 3. Corrupted/invalid data - returns null with error logged
 *
 * @param stored - JSON stringified stored data (encrypted or plaintext)
 * @param password - Password for decryption (same as signature key password)
 * @returns Decrypted data string, original data for legacy, or null if decryption fails
 */
export async function decryptReceiptData(
  stored: string,
  password?: string,
): Promise<string | null> {
  try {
    // Try to parse as encrypted container
    const parsed = JSON.parse(stored) as EncryptedReceiptContainer | unknown;

    // Check if this is an encrypted container
    if (
      parsed &&
      typeof parsed === "object" &&
      "version" in parsed &&
      "encrypted" in parsed &&
      parsed.encrypted === true
    ) {
      const container = parsed as EncryptedReceiptContainer;

      // Ensure we have a password for decryption
      if (!password) {
        if (process.env.NODE_ENV === "development") {
          console.error(
            "[ReceiptEncryption] Cannot decrypt: no password provided",
          );
        }
        throw new Error("Password required to decrypt receipt data");
      }

      // Decrypt the data
      const encryptedData = container.data as EncryptedData;
      const decrypted = await decryptWithPassword(encryptedData, password);
      return decrypted;
    }

    // Legacy unencrypted data: return as-is for backward compatibility
    // This handles version 1 or undefined version data
    return stored;
  } catch (error) {
    // If parsing fails, it might be corrupted data or completely legacy format
    if (process.env.NODE_ENV === "development") {
      console.error("[ReceiptEncryption] Decryption/parsing failed:", error);
    }

    // Attempt to return raw data as fallback (legacy compatibility)
    try {
      // Check if it's valid JSON at all
      JSON.parse(stored);
      // It's valid JSON but not our format - might be legacy
      return stored;
    } catch {
      // Completely invalid data
      return null;
    }
  }
}

/**
 * Check if stored receipt data is encrypted
 *
 * @param stored - JSON stringified stored data
 * @returns true if encrypted, false if plaintext/legacy
 */
export function isReceiptDataEncrypted(stored: string): boolean {
  try {
    const parsed = JSON.parse(stored) as EncryptedReceiptContainer | unknown;
    return !!(
      parsed &&
      typeof parsed === "object" &&
      parsed !== null &&
      "version" in parsed &&
      "encrypted" in parsed &&
      (parsed as EncryptedReceiptContainer).encrypted === true
    );
  } catch {
    return false;
  }
}

/**
 * Migrate legacy unencrypted receipt data to encrypted format
 *
 * @param stored - Current stored data (legacy format)
 * @param password - Password for encryption
 * @returns Encrypted container string or null if migration fails
 */
export async function migrateReceiptDataToEncrypted(
  stored: string,
  password: string,
): Promise<string | null> {
  try {
    // Validate it's actually legacy data (not already encrypted)
    if (isReceiptDataEncrypted(stored)) {
      return stored; // Already encrypted
    }

    // Validate it's proper JSON (receipt data)
    const parsed = JSON.parse(stored);
    if (!parsed || typeof parsed !== "object") {
      throw new Error("Invalid receipt data format");
    }

    // Encrypt the legacy data
    return await encryptReceiptData(stored, password);
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error("[ReceiptEncryption] Migration failed:", error);
    }
    return null;
  }
}
